/**
 * M3 Expressive Feedback Service
 * 
 * Provides multisensory feedback combining:
 * - Sound: Web Audio API oscillator tones inspired by Google's sound design
 * - Haptics: Enhanced Telegram haptic patterns
 * 
 * Design principles from Google's multisensory team:
 * - "Good multisensory design often disappears"
 * - Use silence intentionally - not every action needs feedback
 * - Higher frequency actions = less intrusive feedback
 * - Sound and haptics should tell a clear story
 * 
 * @see https://design.google/library/ux-sound-haptic-material-design
 */

import { telegram } from './telegram';

// =============================================================================
// TYPES
// =============================================================================

export type SoundType = 
  | 'tap'           // Light tap feedback
  | 'success'       // Positive completion
  | 'error'         // Something went wrong
  | 'warning'       // Attention needed
  | 'notification'  // New information
  | 'navigate'      // Navigation action
  | 'toggle'        // Switch/toggle state
  | 'select'        // Selection made
  | 'delete'        // Destructive action
  | 'send'          // Message/action sent
  | 'receive'       // Data received
  | 'levelUp'       // Achievement/progress
  // FAB-specific sounds
  | 'voice'         // Voice/microphone activation
  | 'search'        // Search action
  | 'map'           // Map/location view
  | 'favorite'      // Star/favorite action
  | 'share'         // Share action
  | 'crew'          // Team/group action
  | 'activity'      // Activity/trending
  | 'services'      // Tools/services
  | 'routes'        // Routes/directions
  | 'addLocation'   // Add new location
  | 'ai';           // AI assistant activation

export type HapticType = 
  | 'light'         // Subtle feedback
  | 'medium'        // Standard feedback
  | 'heavy'         // Strong feedback
  | 'rigid'         // Sharp, precise
  | 'soft'          // Gentle, rounded
  | 'success'       // Positive pattern
  | 'warning'       // Attention pattern
  | 'error'         // Negative pattern
  | 'selection';    // Selection changed

interface SoundConfig {
  frequency: number;      // Base frequency in Hz
  duration: number;       // Duration in ms
  type: OscillatorType;   // Wave shape
  volume: number;         // 0-1
  attack?: number;        // Fade in time ms
  decay?: number;         // Fade out time ms
  secondFreq?: number;    // Optional second tone (for intervals)
  secondDelay?: number;   // Delay before second tone ms
}

// =============================================================================
// SOUND CONFIGURATIONS
// =============================================================================

/**
 * Sound configurations inspired by Google's Material sounds
 * Using the key of G (392 Hz) as Google's brand tone
 * Musical intervals create emotional responses:
 * - Octave (2x freq): Uplifting, complete
 * - Fifth (1.5x freq): Stable, positive
 * - Minor third (1.2x freq): Gentle, soft
 */
const SOUNDS: Record<SoundType, SoundConfig> = {
  tap: {
    frequency: 784,        // G5 - high, quick
    duration: 30,
    type: 'sine',
    volume: 0.15,
    attack: 5,
    decay: 25,
  },
  
  success: {
    frequency: 392,        // G4 - the "Super G"
    duration: 150,
    type: 'sine',
    volume: 0.25,
    attack: 10,
    decay: 100,
    secondFreq: 784,       // G5 - octave up (resolution)
    secondDelay: 80,
  },
  
  error: {
    frequency: 220,        // A3 - lower, attention
    duration: 200,
    type: 'triangle',
    volume: 0.2,
    attack: 10,
    decay: 150,
    secondFreq: 196,       // G3 - step down (unresolved)
    secondDelay: 100,
  },
  
  warning: {
    frequency: 440,        // A4 - standard pitch
    duration: 120,
    type: 'triangle',
    volume: 0.18,
    attack: 10,
    decay: 80,
    secondFreq: 440,       // Same note (draw attention)
    secondDelay: 150,
  },
  
  notification: {
    frequency: 523,        // C5 - bright
    duration: 100,
    type: 'sine',
    volume: 0.2,
    attack: 10,
    decay: 70,
    secondFreq: 659,       // E5 - major third up
    secondDelay: 60,
  },
  
  navigate: {
    frequency: 588,        // D5 - forward motion
    duration: 80,
    type: 'sine',
    volume: 0.15,
    attack: 5,
    decay: 60,
  },
  
  toggle: {
    frequency: 698,        // F5
    duration: 50,
    type: 'sine',
    volume: 0.12,
    attack: 5,
    decay: 40,
  },
  
  select: {
    frequency: 659,        // E5
    duration: 60,
    type: 'sine',
    volume: 0.15,
    attack: 5,
    decay: 45,
  },
  
  delete: {
    frequency: 294,        // D4 - lower, finality
    duration: 180,
    type: 'triangle',
    volume: 0.18,
    attack: 10,
    decay: 150,
  },
  
  send: {
    frequency: 523,        // C5
    duration: 100,
    type: 'sine',
    volume: 0.18,
    attack: 10,
    decay: 70,
    secondFreq: 784,       // G5 - fifth up (sent!)
    secondDelay: 50,
  },
  
  receive: {
    frequency: 784,        // G5 - incoming
    duration: 80,
    type: 'sine',
    volume: 0.15,
    attack: 5,
    decay: 60,
    secondFreq: 523,       // C5 - settle down
    secondDelay: 60,
  },
  
  levelUp: {
    frequency: 392,        // G4
    duration: 200,
    type: 'sine',
    volume: 0.22,
    attack: 10,
    decay: 150,
    secondFreq: 523,       // C5 - fourth up
    secondDelay: 100,
  },
  
  // FAB-specific sounds - each designed to match the action's character
  voice: {
    frequency: 440,        // A4 - "listening" tone
    duration: 120,
    type: 'sine',
    volume: 0.2,
    attack: 5,
    decay: 100,
    secondFreq: 554,       // C#5 - rising, expectant
    secondDelay: 60,
  },
  
  search: {
    frequency: 698,        // F5 - bright, curious
    duration: 80,
    type: 'sine',
    volume: 0.18,
    attack: 5,
    decay: 60,
    secondFreq: 880,       // A5 - searching upward
    secondDelay: 40,
  },
  
  map: {
    frequency: 523,        // C5 - open, spatial
    duration: 100,
    type: 'sine',
    volume: 0.18,
    attack: 10,
    decay: 80,
    secondFreq: 392,       // G4 - grounding
    secondDelay: 50,
  },
  
  favorite: {
    frequency: 659,        // E5 - warm, pleasing
    duration: 150,
    type: 'sine',
    volume: 0.2,
    attack: 10,
    decay: 120,
    secondFreq: 784,       // G5 - sparkle up
    secondDelay: 70,
  },
  
  share: {
    frequency: 587,        // D5 - outgoing
    duration: 100,
    type: 'sine',
    volume: 0.18,
    attack: 5,
    decay: 80,
    secondFreq: 740,       // F#5 - expanding
    secondDelay: 50,
  },
  
  crew: {
    frequency: 349,        // F4 - warm, communal
    duration: 120,
    type: 'sine',
    volume: 0.2,
    attack: 10,
    decay: 90,
    secondFreq: 440,       // A4 - harmonious
    secondDelay: 60,
  },
  
  activity: {
    frequency: 622,        // D#5/Eb5 - energetic
    duration: 90,
    type: 'triangle',
    volume: 0.18,
    attack: 5,
    decay: 70,
    secondFreq: 784,       // G5 - upbeat
    secondDelay: 45,
  },
  
  services: {
    frequency: 466,        // A#4/Bb4 - mechanical, helpful
    duration: 100,
    type: 'triangle',
    volume: 0.17,
    attack: 5,
    decay: 80,
    secondFreq: 554,       // C#5 - working
    secondDelay: 50,
  },
  
  routes: {
    frequency: 494,        // B4 - forward motion
    duration: 100,
    type: 'sine',
    volume: 0.18,
    attack: 5,
    decay: 80,
    secondFreq: 622,       // D#5/Eb5 - pathway
    secondDelay: 50,
  },
  
  addLocation: {
    frequency: 392,        // G4 - grounded start
    duration: 130,
    type: 'sine',
    volume: 0.2,
    attack: 10,
    decay: 100,
    secondFreq: 494,       // B4 - adding up
    secondDelay: 65,
  },
  
  ai: {
    frequency: 523,        // C5 - intelligent, bright
    duration: 140,
    type: 'sine',
    volume: 0.2,
    attack: 8,
    decay: 110,
    secondFreq: 698,       // F5 - thinking upward
    secondDelay: 70,
  },
};

// =============================================================================
// FEEDBACK SERVICE CLASS
// =============================================================================

class FeedbackService {
  private audioContext: AudioContext | null = null;
  private isInitialized = false;
  private isEnabled = true;
  private soundEnabled = true;
  private hapticEnabled = true;
  private preloadedBuffers: Map<SoundType, AudioBuffer[]> = new Map();
  private masterGain: GainNode | null = null;
  
  constructor() {
    // Check localStorage for user preferences
    this.loadPreferences();
    // Pre-initialize on first user interaction (required by browsers)
    if (typeof window !== 'undefined') {
      this.setupInitListener();
    }
  }
  
  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================
  
  private setupInitListener(): void {
    const initHandler = () => {
      this.initialize();
      // Remove listeners after first interaction
      ['click', 'touchstart', 'keydown'].forEach(event => {
        document.removeEventListener(event, initHandler);
      });
    };
    
    ['click', 'touchstart', 'keydown'].forEach(event => {
      document.addEventListener(event, initHandler, { once: true, passive: true });
    });
  }
  
  /**
   * Initialize the audio context and preload all sounds
   * Called automatically on first user interaction
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    try {
      // Create AudioContext
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create master gain node
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.85; // Master volume - boosted for outdoor/desert conditions
      this.masterGain.connect(this.audioContext.destination);
      
      // Preload all sounds
      await this.preloadAllSounds();
      
      this.isInitialized = true;
      console.log('[FeedbackService] Initialized with all sounds preloaded');
    } catch (error) {
      console.warn('[FeedbackService] Failed to initialize audio:', error);
    }
  }
  
  /**
   * Preload all sound configurations into AudioBuffers
   * This ensures instant playback even in low-signal desert conditions
   */
  private async preloadAllSounds(): Promise<void> {
    if (!this.audioContext) return;
    
    const soundTypes = Object.keys(SOUNDS) as SoundType[];
    
    for (const soundType of soundTypes) {
      const config = SOUNDS[soundType];
      const buffers: AudioBuffer[] = [];
      
      // Generate primary tone buffer
      buffers.push(await this.generateToneBuffer(config));
      
      // Generate secondary tone buffer if exists
      if (config.secondFreq) {
        buffers.push(await this.generateToneBuffer({
          ...config,
          frequency: config.secondFreq,
        }));
      }
      
      this.preloadedBuffers.set(soundType, buffers);
    }
  }
  
  /**
   * Generate an AudioBuffer for a tone configuration
   */
  private async generateToneBuffer(config: SoundConfig): Promise<AudioBuffer> {
    if (!this.audioContext) throw new Error('AudioContext not initialized');
    
    const sampleRate = this.audioContext.sampleRate;
    const duration = config.duration / 1000; // Convert to seconds
    const length = Math.ceil(sampleRate * duration);
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    
    const attack = (config.attack || 10) / 1000;
    const decay = (config.decay || config.duration) / 1000;
    
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      
      // Generate waveform based on type
      let sample: number;
      const phase = 2 * Math.PI * config.frequency * t;
      
      switch (config.type) {
        case 'sine':
          sample = Math.sin(phase);
          break;
        case 'triangle':
          sample = 2 * Math.abs(2 * (t * config.frequency - Math.floor(t * config.frequency + 0.5))) - 1;
          break;
        case 'square':
          sample = Math.sin(phase) > 0 ? 1 : -1;
          break;
        case 'sawtooth':
          sample = 2 * (t * config.frequency - Math.floor(t * config.frequency + 0.5));
          break;
        default:
          sample = Math.sin(phase);
      }
      
      // Apply envelope (attack/decay)
      let envelope = 1;
      if (t < attack) {
        envelope = t / attack; // Attack ramp up
      } else if (t > duration - decay) {
        envelope = (duration - t) / decay; // Decay ramp down
      }
      
      data[i] = sample * config.volume * envelope;
    }
    
    return buffer;
  }
  
  // ===========================================================================
  // PREFERENCES
  // ===========================================================================
  
  private loadPreferences(): void {
    if (typeof localStorage === 'undefined') return;
    
    const prefs = localStorage.getItem('feedback_preferences');
    if (prefs) {
      try {
        const parsed = JSON.parse(prefs);
        this.isEnabled = parsed.enabled ?? true;
        this.soundEnabled = parsed.sound ?? true;
        this.hapticEnabled = parsed.haptic ?? true;
      } catch (e) {
        // Use defaults
      }
    }
  }
  
  private savePreferences(): void {
    if (typeof localStorage === 'undefined') return;
    
    localStorage.setItem('feedback_preferences', JSON.stringify({
      enabled: this.isEnabled,
      sound: this.soundEnabled,
      haptic: this.hapticEnabled,
    }));
  }
  
  /**
   * Enable or disable all feedback
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.savePreferences();
  }
  
  /**
   * Enable or disable sound feedback
   */
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = enabled;
    this.savePreferences();
  }
  
  /**
   * Enable or disable haptic feedback
   */
  setHapticEnabled(enabled: boolean): void {
    this.hapticEnabled = enabled;
    this.savePreferences();
  }
  
  /**
   * Get current preferences
   */
  getPreferences(): { enabled: boolean; sound: boolean; haptic: boolean } {
    return {
      enabled: this.isEnabled,
      sound: this.soundEnabled,
      haptic: this.hapticEnabled,
    };
  }
  
  // ===========================================================================
  // SOUND PLAYBACK
  // ===========================================================================
  
  /**
   * Play a sound effect
   * @param type - The type of sound to play
   */
  playSound(type: SoundType): void {
    if (!this.isEnabled || !this.soundEnabled) return;
    if (!this.audioContext || !this.masterGain) {
      // Try to initialize if not already
      this.initialize();
      return;
    }
    
    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    const buffers = this.preloadedBuffers.get(type);
    const config = SOUNDS[type];
    
    if (!buffers || buffers.length === 0) {
      // Fallback to real-time generation if buffer not preloaded
      this.playSoundRealtime(config);
      return;
    }
    
    // Play primary buffer
    this.playBuffer(buffers[0]);
    
    // Play secondary buffer with delay if exists
    if (buffers.length > 1 && config.secondDelay) {
      setTimeout(() => {
        this.playBuffer(buffers[1]);
      }, config.secondDelay);
    }
  }
  
  /**
   * Play a preloaded AudioBuffer
   */
  private playBuffer(buffer: AudioBuffer): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);
    source.start();
  }
  
  /**
   * Fallback: Play sound in real-time using oscillators
   */
  private playSoundRealtime(config: SoundConfig): void {
    if (!this.audioContext || !this.masterGain) return;
    
    const now = this.audioContext.currentTime;
    const duration = config.duration / 1000;
    const attack = (config.attack || 10) / 1000;
    
    // Create oscillator
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.type = config.type;
    oscillator.frequency.value = config.frequency;
    
    // Apply envelope
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(config.volume, now + attack);
    gainNode.gain.linearRampToValueAtTime(0, now + duration);
    
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain);
    
    oscillator.start(now);
    oscillator.stop(now + duration);
    
    // Play second tone if configured
    if (config.secondFreq && config.secondDelay) {
      const delay = config.secondDelay / 1000;
      const osc2 = this.audioContext.createOscillator();
      const gain2 = this.audioContext.createGain();
      
      osc2.type = config.type;
      osc2.frequency.value = config.secondFreq;
      
      gain2.gain.setValueAtTime(0, now + delay);
      gain2.gain.linearRampToValueAtTime(config.volume, now + delay + attack);
      gain2.gain.linearRampToValueAtTime(0, now + delay + duration);
      
      osc2.connect(gain2);
      gain2.connect(this.masterGain);
      
      osc2.start(now + delay);
      osc2.stop(now + delay + duration);
    }
  }
  
  // ===========================================================================
  // HAPTIC FEEDBACK
  // ===========================================================================
  
  /**
   * Trigger haptic feedback via Telegram WebApp API
   * @param type - The type of haptic pattern
   */
  haptic(type: HapticType): void {
    if (!this.isEnabled || !this.hapticEnabled) return;
    
    // Map our extended types to Telegram's supported types
    // Telegram supports: light, medium, heavy, success, error, warning
    const telegramTypeMap: Record<HapticType, 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning'> = {
      light: 'light',
      medium: 'medium',
      heavy: 'heavy',
      rigid: 'heavy',      // Map to heavy for sharp feedback
      soft: 'light',       // Map to light for gentle feedback
      success: 'success',
      warning: 'warning',
      error: 'error',
      selection: 'light',  // Map to light for selection feedback
    };
    
    const telegramType = telegramTypeMap[type];
    
    try {
      // Use the telegram service's hapticFeedback method which handles version checks
      telegram.hapticFeedback(telegramType);
    } catch (error) {
      // Haptic not available (not in Telegram or device doesn't support)
      console.debug('[FeedbackService] Haptic feedback not available');
    }
  }
  
  // ===========================================================================
  // COMBINED FEEDBACK (SOUND + HAPTIC)
  // ===========================================================================
  
  /**
   * Trigger coordinated sound and haptic feedback
   * This creates the multisensory experience Google recommends
   */
  feedback(sound: SoundType, haptic: HapticType): void {
    this.playSound(sound);
    this.haptic(haptic);
  }
  
  // ===========================================================================
  // PRESET FEEDBACK PATTERNS
  // ===========================================================================
  
  /**
   * Light tap feedback - for buttons, selections
   */
  tap(): void {
    this.feedback('tap', 'light');
  }
  
  /**
   * Success feedback - for completed actions
   */
  success(): void {
    this.feedback('success', 'success');
  }
  
  /**
   * Error feedback - for failed actions
   */
  error(): void {
    this.feedback('error', 'error');
  }
  
  /**
   * Warning feedback - for attention-needed states
   */
  warning(): void {
    this.feedback('warning', 'warning');
  }
  
  /**
   * Navigation feedback - for route/page changes
   */
  navigate(): void {
    this.feedback('navigate', 'medium');
  }
  
  /**
   * Toggle feedback - for switches, checkboxes
   */
  toggle(): void {
    this.feedback('toggle', 'rigid');
  }
  
  /**
   * Selection feedback - for list items, options
   */
  select(): void {
    this.feedback('select', 'selection');
  }
  
  /**
   * Delete feedback - for destructive actions
   */
  delete(): void {
    this.feedback('delete', 'heavy');
  }
  
  /**
   * Send feedback - for outgoing data/messages
   */
  send(): void {
    this.feedback('send', 'medium');
  }
  
  /**
   * Receive feedback - for incoming data
   */
  receive(): void {
    this.feedback('receive', 'soft');
  }
  
  /**
   * Level up/achievement feedback - for progress milestones
   */
  levelUp(): void {
    this.feedback('levelUp', 'success');
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const feedbackService = new FeedbackService();

// Convenience exports for direct use
export const {
  tap,
  success,
  error,
  warning,
  navigate,
  toggle,
  select,
  send,
  receive,
  levelUp,
  // FAB sounds
  voice,
  search,
  map,
  favorite,
  share,
  crew,
  activity,
  services,
  routes,
  addLocation,
} = {
  tap: () => feedbackService.tap(),
  success: () => feedbackService.success(),
  error: () => feedbackService.error(),
  warning: () => feedbackService.warning(),
  navigate: () => feedbackService.navigate(),
  toggle: () => feedbackService.toggle(),
  select: () => feedbackService.select(),
  send: () => feedbackService.send(),
  receive: () => feedbackService.receive(),
  levelUp: () => feedbackService.levelUp(),
  // FAB sounds with haptic
  voice: () => feedbackService.feedback('voice', 'medium'),
  search: () => feedbackService.feedback('search', 'light'),
  map: () => feedbackService.feedback('map', 'light'),
  favorite: () => feedbackService.feedback('favorite', 'medium'),
  share: () => feedbackService.feedback('share', 'medium'),
  crew: () => feedbackService.feedback('crew', 'light'),
  activity: () => feedbackService.feedback('activity', 'light'),
  services: () => feedbackService.feedback('services', 'light'),
  routes: () => feedbackService.feedback('routes', 'light'),
  addLocation: () => feedbackService.feedback('addLocation', 'medium'),
};

export default feedbackService;
